// ── useProfile ─────────────────────────────────────────────────────────
// Hook to load/save a user's row in user_profiles. Returns:
//   profile     — current profile row or null (not-yet-onboarded)
//   loading     — initial fetch in flight
//   error       — last error from load or save
//   saveProfile — upserts the given fields (requires first/last/age/nat/gender)
//   updateProfile — partial update (e.g. just avatar_url or preferred_mode)
//   refresh     — re-fetch from DB
//   uploadAvatar — upload a File to profile-photos bucket, return public URL
//
// Notes:
//   • Reads from user_profiles where user_id = auth.uid().
//   • Guards every write with the current user id — RLS enforces this too
//     but we also validate client-side to fail fast.
//   • uploadAvatar writes to `{user_id}/avatar-{timestamp}.{ext}` to bust
//     cache on replacement (CDN's public URL stays unique per upload).
// ───────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../AuthContext'

const PROFILE_PHOTOS_BUCKET = 'profile-photos'

// Single source of truth for safe, user-visible profile columns
const PROFILE_COLUMNS = 'user_id, first_name, last_name, age, nationality, gender, avatar_url, preferred_mode, created_at, updated_at'

export function useProfile(targetUserId = null) {
  const { user } = useAuth()
  // When targetUserId is provided, load that user's profile (view mode);
  // otherwise load the current auth user's profile.
  const effectiveUserId = targetUserId || user?.id || null
  const isOwnProfile = !targetUserId || (user && targetUserId === user.id)

  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(!!effectiveUserId)
  const [error, setError] = useState(null)
  const cancelRef = useRef({ cancelled: false })

  const load = useCallback(async () => {
    if (!effectiveUserId) { setProfile(null); setLoading(false); return }
    cancelRef.current = { cancelled: false }
    const token = cancelRef.current
    setLoading(true); setError(null)

    const { data, error: err } = await supabase
      .from('user_profiles')
      .select(PROFILE_COLUMNS)
      .eq('user_id', effectiveUserId)
      .maybeSingle()

    if (token.cancelled) return
    if (err) { setError(err); setLoading(false); return }
    setProfile(data || null)
    setLoading(false)
  }, [effectiveUserId])

  useEffect(() => {
    load()
    return () => { cancelRef.current.cancelled = true }
  }, [load])

  // Full upsert — used by onboarding form (all required fields)
  const saveProfile = useCallback(async (fields) => {
    if (!user?.id) return { error: new Error('Not authenticated') }
    if (!isOwnProfile) return { error: new Error('Cannot save another user\'s profile') }

    const row = { user_id: user.id, ...fields }
    const { data, error: err } = await supabase
      .from('user_profiles')
      .upsert(row, { onConflict: 'user_id' })
      .select(PROFILE_COLUMNS)
      .single()

    if (err) { setError(err); return { error: err } }
    setProfile(data)
    return { data }
  }, [user, isOwnProfile])

  // Partial update — used for avatar-only, preferred_mode-only, etc.
  const updateProfile = useCallback(async (patch) => {
    if (!user?.id) return { error: new Error('Not authenticated') }
    if (!isOwnProfile) return { error: new Error('Cannot update another user\'s profile') }

    const { data, error: err } = await supabase
      .from('user_profiles')
      .update(patch)
      .eq('user_id', user.id)
      .select(PROFILE_COLUMNS)
      .single()

    if (err) { setError(err); return { error: err } }
    setProfile(data)
    return { data }
  }, [user, isOwnProfile])

  // Upload avatar to storage bucket, return public URL.
  // Bucket is public-read so we don't need signed URLs.
  const uploadAvatar = useCallback(async (file) => {
    if (!user?.id) return { error: new Error('Not authenticated') }
    if (!file) return { error: new Error('No file provided') }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      return { error: new Error('Photo must be JPEG, PNG, or WebP') }
    }
    if (file.size > 2 * 1024 * 1024) {
      return { error: new Error('Photo must be under 2 MB') }
    }

    const ext = file.name.split('.').pop().toLowerCase()
    // Include timestamp to avoid CDN cache on replacement; folder = user id.
    const path = `${user.id}/avatar-${Date.now()}.${ext}`

    const { error: uploadErr } = await supabase.storage
      .from(PROFILE_PHOTOS_BUCKET)
      .upload(path, file, { upsert: true, cacheControl: '3600' })

    if (uploadErr) { setError(uploadErr); return { error: uploadErr } }

    const { data } = supabase.storage.from(PROFILE_PHOTOS_BUCKET).getPublicUrl(path)
    return { publicUrl: data.publicUrl, path }
  }, [user])

  return {
    profile,
    loading,
    error,
    isOwnProfile,
    saveProfile,
    updateProfile,
    uploadAvatar,
    refresh: load,
  }
}
